'use client'

import { useRef, useState } from 'react'
import { ImagePlus, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

export type ForumAttachmentInput = {
  data: string
  name: string
}

const MAX_BYTES = 3 * 1024 * 1024
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export function ForumAttachmentPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: ForumAttachmentInput | null
  onChange: (value: ForumAttachmentInput | null) => void
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [reading, setReading] = useState(false)
  const [error, setError] = useState('')

  async function selectFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setError('')
    if (!ALLOWED_TYPES.has(file.type)) {
      setError('Choose a JPEG, PNG, or WebP image.')
      return
    }
    if (file.size > MAX_BYTES) {
      setError('Images must be 3 MB or smaller.')
      return
    }

    setReading(true)
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('Could not read that image.'))
        reader.readAsDataURL(file)
      })
      onChange({ data, name: file.name })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that image.')
    } finally {
      setReading(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          disabled={disabled || reading}
          onChange={selectFile}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || reading}
          onClick={() => inputRef.current?.click()}
        >
          {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {value ? 'Replace image' : 'Attach image'}
        </Button>
        <span className="text-xs text-muted-foreground">
          JPEG, PNG, or static WebP · up to 3 MB
        </span>
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Images stay private until a moderator approves them.
      </p>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {value && (
        <div className="relative overflow-hidden rounded-xl border border-border bg-background">
          <img
            src={value.data}
            alt="Selected attachment preview"
            className="max-h-64 w-full object-contain"
          />
          <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2">
            <span className="min-w-0 truncate text-xs text-muted-foreground">{value.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              disabled={disabled}
              onClick={() => onChange(null)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
